// src/pages/ContactMe.tsx
import React, { useEffect, useState, useRef } from 'react';
import emailjs from '@emailjs/browser'; 
import ReCAPTCHA from 'react-google-recaptcha';  
import './ContactMe.css';
import profilePic from '../images/gunjan.jpg';
import { FaEnvelope, FaGithub, FaLinkedin } from 'react-icons/fa'; 
import { ContactMe as IContactMe } from '../types';
import { getContactMe } from '../queries/getContactMe';

// ✅ Use ENV variables, never hardcode
const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID!;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID!;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY!;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY!; 

const ContactMe: React.FC = () => {
    const [userData, setUserData] = useState<IContactMe | null>(null);
    const form = useRef<HTMLFormElement>(null); 
    const [formStatus, setFormStatus] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false); 
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);

    useEffect(() => {
        async function fetchUserData() {
            const data = await getContactMe();
            setUserData(data);
        }
        fetchUserData();
    }, []);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // ✅ Check 1: Form is ready and captcha has been completed (token is not null)
        if (!form.current || !captchaToken) {
            setFormStatus('Please complete the captcha.');
            return;
        }

        if (isSubmitting) return; 
        setIsSubmitting(true);
        setFormStatus('Sending...');

        try {
            // ✅ Sanitization before sending
            const formData = new FormData(form.current);
            const sanitizedData = {
                user_name: formData.get("user_name")?.toString().trim(),
                user_email: formData.get("user_email")?.toString().trim(),
                message: formData.get("message")?.toString().trim(),
                // 🔥 FIX: Manually add the V2 token to the payload for EmailJS verification
                'g-recaptcha-response': captchaToken,
            };

            await emailjs.send(SERVICE_ID, TEMPLATE_ID, sanitizedData, PUBLIC_KEY);

            setFormStatus('Message sent successfully! I will get back to you soon.');
            form.current.reset();
            setCaptchaToken(null);
        } catch (error: any) {
            console.error('EmailJS Error:', error);
            // Provide a better error message if the error object contains one
            const errorMessage = error.text 
                ? `Failed to send message. Reason: ${error.text}` 
                : 'Failed to send message. Please try again later.';
            setFormStatus(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!userData) return <div>Loading...</div>;

    return (
        <div className="contact-container">
            {/* Existing Profile Section */}
            <div className="contact-header">
                <p>I'm always up for a chat or a coffee! Feel free to reach out.</p>
            </div>
            
            <div className="linkedin-badge-custom">
                <img 
                    src={userData.profilePicture?.url || profilePic} 
                    alt={userData.name || "User Profile"} 
                    className="badge-avatar" 
                />
                <div className="badge-content">
                    <h3 className="badge-name">{userData.name}</h3>
                    <p className="badge-title">{userData.title}</p>
                    <p className="badge-description">{userData.summary}</p>
                    <p className="badge-company">{userData.companyUniversity}</p>
                    <a href={userData.linkedinLink} target="_blank" rel="noopener noreferrer" className="badge-link">
                        <FaLinkedin className="linkedin-icon" /> View Profile
                    </a>
                </div>
            </div>

            <div className="contact-details">
                <div className="contact-item">
                    <FaEnvelope className="contact-icon" />
                    <a href={`mailto:${userData.email}`} className="contact-link">{userData.email}</a>
                </div>
                <div className="contact-item">
                    <FaGithub className="contact-icon" />
                    <a href={userData.githubLink} target="_blank" rel="noopener noreferrer" className="contact-link">
                        View GitHub Profile
                    </a>
                </div>
            </div>

            {/* Contact Form */}
            <div className="form-intro-header">
                <p>Alternately fill up the form and I will get back to you.</p>
            </div>
            
            <form ref={form} onSubmit={handleSubmit} className="contact-form-card">
                <label htmlFor="user_name">Your Name</label>
                <input type="text" id="user_name" name="user_name" required />

                <label htmlFor="user_email">Your Email</label>
                <input type="email" id="user_email" name="user_email" required />

                <label htmlFor="message">Your Message</label>
                <textarea id="message" name="message" rows={5} required></textarea>

                {/* ✅ Add reCAPTCHA */}
                <div className="recaptcha-container">
                <ReCAPTCHA 
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={(token) => setCaptchaToken(token)}
                        size="normal"
                        theme="dark"
                />
                </div>


                <button 
                    type="submit" 
                    className="form-submit-button" 
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "Sending..." : "Send Message"}
                </button>

                {formStatus && (
                    <p className={`form-status ${formStatus.includes('successfully') ? 'success' : 'error'}`}>
                        {formStatus}
                    </p>
                )}
            </form>
        </div>
    );
};

export default ContactMe;